import React, { Fragment, useContext } from 'react';
import { produce } from 'immer';
import {
  type AllConditions,
  type ConditionProperties,
} from 'json-rules-engine';
import { get, set, unset } from 'lodash';
import { SBadge, SButton, SIcon } from '@chargebee/sting-react';
import { AndOrPillButton } from '@bbkAdminComponents/experiences/audiences/edit-rule/AndOrPillButton';
import { ConditionItem } from '@bbkAdminComponents/experiences/audiences/edit-rule/condition-item';
import { EditRuleContext } from '@bbkAdminComponents/experiences/audiences/edit-rule/edit-rule-context';
import {
  getGroupConditions,
  getGroupOperator,
  getGroupType,
  GroupOperator,
  GroupType,
  isGroup,
} from '@bbkAdminComponents/experiences/audiences/edit-rule/rules-utils';
import { cs } from '@bbkAdminUtils/css-utils';
import { type PatchPath } from '@bbkAdminUtils/utility-types';

type ConditionGroupProps = {
  renderBorder?: boolean;
  // Maximum remaining nesting levels allowed for sub-groups. Decremented when recursing.
  // When 1, adding new groups is disabled.
  maxLevels?: number;
  // Current nesting level (root = 0). Used for display/indentation and recursion guard.
  level?: number;
  path: PatchPath;
  // If true, disallows changing the grouping operator (AND/OR)
  disallowOperatorChange?: boolean;
};

// ATTN: Keep this as function, recursion doesn't work with const variables
export function ConditionsGroup(props: ConditionGroupProps) {
  const {
    path,
    renderBorder,
    maxLevels = 1,
    level = 0,
    disallowOperatorChange = false,
  } = props;

  const { rule, setRule } = useContext(EditRuleContext);

  const group = get(rule, path);
  const groupOperator = getGroupOperator(group);
  const groupConditions = getGroupConditions(group);
  const groupPath = [...path, groupOperator];

  const updateGroupingOperator = (checked: boolean) => {
    const newGroupingOperator: GroupOperator = checked
      ? GroupOperator.all
      : GroupOperator.any;

    const updatedRule = produce(rule, (draft) => {
      // swapping operators all|any
      set(draft, [...path, newGroupingOperator], get(draft, groupPath));

      unset(draft, groupPath);
    });

    setRule(updatedRule);
  };

  const addGroup = () => {
    const updatedRule = produce(rule, (draft) => {
      const curGroup = get(draft, groupPath);

      const newGroup: AllConditions = {
        all: [
          {
            fact: '',
            operator: '',
            value: '',
          },
        ],
      };
      curGroup.push(newGroup);
    });

    setRule(updatedRule);
  };

  const addCondition = () => {
    const updatedRule = produce(rule, (draft) => {
      const curGroup = get(draft, groupPath);

      const newCondition: ConditionProperties = {
        fact: '',
        operator: '',
        value: '',
      };
      curGroup.push(newCondition);
    });

    setRule(updatedRule);
  };

  return (
    <div
      className={cs('tw-w-full tw-rounded', {
        'tw-border tw-border-neutral-100 tw-p-5 tw-pr-0': renderBorder,
      })}
    >
      {groupConditions.length === 0 && (
        <div className="tw-pb-4 tw-text-neutral-500">No rules defined!</div>
      )}
      {groupConditions.map((condition, idx, arr) => {
        const conditionPath = [...path, groupOperator, idx];
        return (
          <Fragment key={conditionPath.join('.')}>
            {isGroup(condition) ? (
              <ConditionsGroup
                path={conditionPath}
                renderBorder
                maxLevels={maxLevels - 1}
                level={level + 1}
                disallowOperatorChange={disallowOperatorChange}
              />
            ) : (
              <ConditionItem condition={condition} path={conditionPath} />
            )}
            {idx < arr.length - 1 && (
              <SBadge variant="neutral" className="tw-my-4">
                {getGroupType(group)}
              </SBadge>
            )}
          </Fragment>
        );
      })}
      <div className="tw-flex tw-flex-col tw-gap-4 tw-pt-4">
        {!disallowOperatorChange && groupConditions.length > 1 && (
          <AndOrPillButton
            value={getGroupType(group)}
            onChange={(v) => updateGroupingOperator(v === GroupType.AND)}
            disabled={disallowOperatorChange}
          />
        )}
        <div>
          <SButton
            variant="primary-ghost"
            icon={<SIcon name="plus" />}
            onClick={addCondition}
          >
            Add another condition
          </SButton>
          {maxLevels > 1 && (
            <SButton
              variant="primary-ghost"
              icon={<SIcon name="plus" />}
              onClick={addGroup}
            >
              Group of rules
            </SButton>
          )}
        </div>
      </div>
    </div>
  );
}
